import React, { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled, { useTheme } from 'styled-components'
import { useDropOffLocationContext, useModalContext } from '../../core/application/contexts'
import { ISearchTerm } from '../../core/application/contexts/ConfigurationContext'
import { DropOffLocation, WasteType } from '../../core/domain/models'
import { FilterIcon, RightArrowIcon } from '../icons'
import Loader from '../loader/Loader'
import AvailableWasteTypesModal from '../modal/availableWasteTypeModal/AvailableWasteTypesModal'
import ModalWithCloseCross from '../modal/ModalWithCloseCross'
import DropOffLocationItem from './DropOffLocationItem'
import DropOffLocationSearchInput from './DropOffLocationSearchInput'
import MostPopularSearches from './MostPopularSearches'

export interface IDropOffLocationDetailsProps {
  dropOffLocations: DropOffLocation[]
  onSelectItem?: (item: DropOffLocation) => void
  isSearching: boolean
  wasteTypeList: WasteType[]
  mostPopularSearchTerms?: Array<ISearchTerm>
}

const DropOffLocationList: React.FC<IDropOffLocationDetailsProps> = (props) => {
  const { onSelectItem, isSearching, dropOffLocations, mostPopularSearchTerms = [], wasteTypeList } = props
  const intl = useIntl()
  const theme = useTheme()
  const { setWasteTypeFilterModal } = useModalContext()
  const [availableWasteModalOpen, setAvailableWasteModalOpen] = useState<boolean>(false)
  const [dropOffLocation, setDropOffLocation] = useState<DropOffLocation | null>(null)
  const { wasteTypeFilters, queryLoading } = useDropOffLocationContext()

  const onCloseModal = useCallback(() => {
    setAvailableWasteModalOpen(false)
    setDropOffLocation(null)
  }, [])

  const onShowWastes = useCallback((location: DropOffLocation) => {
    setDropOffLocation(location)
    setAvailableWasteModalOpen(true)
  }, [])

  const onClickFilter = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    setWasteTypeFilterModal(true)
  }

  const handleTagOnClick = (value: string) => {
    console.log('filter with > ', value)
  }

  const findWasteTypesByName = (wasteTypeList: Array<WasteType>, wasteTypeIds: Array<string>): Array<WasteType> => {
    const wasteTypes: Array<WasteType> = []
    for (const wasteType of wasteTypeList) {
      if (wasteTypeIds.includes(wasteType.id)) {
        wasteTypes.push(wasteType)
      }
    }
    return wasteTypes
  }

  const dropOffLocationWasteTypes = dropOffLocation
    ? wasteTypeList.filter((wasteType: WasteType) => dropOffLocation.acceptedWasteIds.includes(wasteType.id))
    : []

  const filterLabel = useMemo(() => {
    if (wasteTypeFilters.length === wasteTypeList.length) {
      return <FormattedMessage id='DropOffLocation.FilterButton.All' />
    } else if (wasteTypeFilters.length) {
      const wasteTypeLabels = wasteTypeList.filter((wasteType) => wasteTypeFilters.includes(wasteType.id))
      return wasteTypeLabels.map(it => it.name).slice(0, 3).join(', ')
    }
    return <FormattedMessage id='DropOffLocation.FilterButton.Label' />
  }, [wasteTypeList, wasteTypeFilters])

  return (
    <Container>
      <Title>
        <FormattedMessage id='DropOffLocation.list.title' />
      </Title>
      <SearchInputWrapper>
        <DropOffLocationSearchInput
          placeholder={intl.formatMessage({ id: 'DropOffLocation.search.searchPlaceholder' })}
        />
      </SearchInputWrapper>
      {mostPopularSearchTerms.length > 0 && (
        <MostPopularSearches onClick={handleTagOnClick} terms={mostPopularSearchTerms} />
      )}
      <FilterButton type='button' onClick={onClickFilter}>
        <FilterButtonIconWrapper>
          <FilterIcon />
        </FilterButtonIconWrapper>
        <FilterButtonLabel>{filterLabel}</FilterButtonLabel>
        <FilterArrowIconWrapper>
          <RightArrowIcon />
        </FilterArrowIconWrapper>
      </FilterButton>
      <Header>
        {isSearching ? (
          <FormattedMessage id='DropOffLocation.list.results' />
        ) : (
          dropOffLocations.length > 0 && <FormattedMessage id='DropOffLocation.list.suggested' />
        )}
      </Header>
      <ListWrapper>
        {queryLoading ? (
          <LoaderWrapper>
            <Loader size={70} color={theme.color.primary} />
          </LoaderWrapper>
        ) : (
          dropOffLocations.slice(0, 20).map((dropOffLocation) => {
            const { storeId, address, name, acceptedWasteIds } = dropOffLocation
            const wasteTypes = findWasteTypesByName(wasteTypeList, acceptedWasteIds)
            return (
              <DropOffLocationItem
                key={storeId}
                name={name}
                address={address}
                picture={dropOffLocation.firstPicture}
                onClick={() => {
                  onSelectItem && onSelectItem(dropOffLocation)
                }}
                wasteTypes={wasteTypes}
                onShowAll={() => {
                  onShowWastes(dropOffLocation)
                }}
              />
            )
          })
        )}
        {dropOffLocation && (
          <ModalWithCloseCross open={availableWasteModalOpen} onClose={onCloseModal}>
            <AvailableWasteTypesModal
              wasteTypes={dropOffLocationWasteTypes}
              onClose={onCloseModal}
              name={dropOffLocation.name}
            />
          </ModalWithCloseCross>
        )}
      </ListWrapper>
    </Container>
  )
}
const ListWrapper = styled.div`
  overflow-y: auto;
  flex-grow: 1;

  &::-webkit-scrollbar {
    display: none;
  }
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  flex-grow: 1;
`

const SearchInputWrapper = styled.div`
  height: 48px;
  width: 100%;
  margin-bottom: 16px;
`

const Title = styled.h1`
  font-weight: 400;
  font-size: ${({ theme }) => theme.size.big};
  line-height: 31px;
`

const Header = styled.h2`
  font-weight: 600;
  font-size: ${({ theme }) => theme.size.bigger};
  line-height: 30px;
`

const FilterButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  gap: 12px;
  height: 48px;
  background-color: #f2f2f2;
  border-radius: 4px;
  line-height: 18px;
  margin-bottom: 24px;
  cursor: pointer;
`

const FilterButtonIconWrapper = styled.div`
  width: 24px;
`

const FilterArrowIconWrapper = styled.div`
  width: 8px;
`

const FilterButtonLabel = styled.div`
  flex-grow: 1;
  text-align: left;
  font-style: normal;
  font-weight: ${({ theme }) => theme.weight.normal};
  font-size: ${({ theme }) => theme.size.small};
  color: ${({ theme }) => theme.color.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

export default DropOffLocationList
